
import * as THREE from 'three';
import { Pane } from 'tweakpane';
import Background from "./background"
import VideoGrid from "./video-grid"

function trackDirection(value) { // kept for reference, no longer used
  typeof value !== "object" && (value = { onUpdate: value });
  let prevTime = 0,
    prevReversed = false,
    anim = value.eventCallback ? value : value.animation,
    onUpdate = value.onUpdate,
    onToggle = value.onToggle;
  return anim
    ? anim.eventCallback(
        "onUpdate",
        trackDirection({ onUpdate: onUpdate, onToggle: onToggle })
      )
    : function () {
        let time = this.totalTime(),
          reversed = time < prevTime;
        this.direction = reversed ? -1 : 1;
        if (reversed !== prevReversed) {
          onToggle && onToggle.call(this, this.direction);
          prevReversed = reversed;
        }
        prevTime = time;
        onUpdate && onUpdate.call(this, this.direction);
      };
}

export default class Scene {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera( 60, 1, 0.1, 60 );
    renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
    });
    intersectable = false;
    pointer = new THREE.Vector2();
    components = {};
    PARAMS = {
        x: 0,
        y: 0,
        z: 0,
        a: 1
    }
    currentIndex = 0;
    isMuted = true;

    constructor({ el }) {
        this.$graphics = el
        this.videoTextures = []

        this.renderer.setClearAlpha( this.PARAMS.a );

    this.initialize();
        this.setComponents();

        this.handleResize();
        this.boundResize = this.handleResize.bind(this);
        ScrollTrigger.addEventListener("refresh", this.boundResize)
        window.addEventListener("resize", this.boundResize);
        // Chrome for iPhone and in-app browsers (Google app) start with their
        // toolbars shown, so the pinned holder is measured short. When the
        // toolbars hide the screen grows but ScrollTrigger ignores height-only
        // resizes on touch devices below 25% of the screen, so the holder kept
        // its first height and a black band showed below it. Grow it to the
        // screen and re-measure once, after the toolbar animation.
        this.boundFitViewport = () => {
            clearTimeout(this.fitTimer)
            this.fitTimer = setTimeout(() => this.fitViewport(), 250)
        }
        window.addEventListener("resize", this.boundFitViewport);
        this.fitWidth = window.innerWidth
        this.fitScreen = window.innerHeight

        this.renderer.setAnimationLoop(this.hanedleRender.bind(this));

        const controls = this.$graphics.querySelector(".toggle-play-pause")

        controls.addEventListener("click", () => {
            const htmlVideos = this.$graphics.parentElement.parentElement.querySelectorAll("video")
            controls.classList.toggle("paused")
            if (controls.classList.contains("paused")) {
                htmlVideos.forEach(video => video.pause())
            } else {
                htmlVideos.forEach(video => video.play())
            }
        })
        const soundControls = this.$graphics.querySelector(".toggle-sound")
        const htmlVideos = this.$graphics.parentElement.parentElement.querySelectorAll("video")
        if (soundControls) {
            soundControls.addEventListener("click", () => {
                soundControls.classList.toggle("muted")
                this.isMuted = soundControls.classList.contains("muted")
                
                // Sync the volume audio states immediately
                this.syncVideoAudio(htmlVideos)
            })
        }
    }
    syncVideoAudio(htmlVideos) {
        htmlVideos.forEach((video, idx) => {
            if (idx === this.currentIndex && !this.isMuted) {
                video.muted = false;
                video.volume = 1.0;
            } else {
                video.muted = true;
            }
        });
    }

    setComponents() {
        const { scene, renderer } = this;
        const htmlVideos = this.$graphics.parentElement.parentElement.querySelectorAll("video")
        this.syncVideoAudio(htmlVideos)
        this.components.videoGrid = new VideoGrid({ videos: htmlVideos })
        this.components.background = new Background({ scene, renderer })

        this.components.background.material.uniforms.tDiffuse.value = this.components.videoGrid.videoTextures[0]

        // The video crossfades used to be GSAP tweens scrubbed by ScrollTrigger,
        // with the textures swapped in tween callbacks. When a page transition
        // inserts the home page and ScrollTrigger re-measures it, those
        // callbacks are skipped and the section could start on the last video
        // with the crossfade stuck at its end (client feedback round 2, item
        // 13). The state is now computed from the scroll progress directly.
        this.htmlVideos = htmlVideos
        this.applyProgress(0)

        this.setCaptions(htmlVideos.length)

        this.st = ScrollTrigger.create({
            trigger: this.$graphics,
            start: 'top top',
            pinType: 'fixed',
            pin: true,
            end: 'bottom bottom',
            endTrigger:  this.$graphics.parentElement,
            pinSpacing: false,
            onUpdate: (self) => this.applyProgress(self.progress),
            onRefresh: (self) => this.applyProgress(self.progress)
        });


        
        
    }

    // Phones only: copy each category's name and link into the pinned holder
    // and cross-fade them with the videos, so a caption never scrolls over the
    // video of another category or into the fixed header (client feedback #4).
    setCaptions(count) {
        this.captions = []
        if (!window.matchMedia("(max-width: 768px)").matches) return

        const block = this.$graphics.closest(".wp-block-cinetic-creatives-blocks-categories")
        const items = this.$graphics.parentElement.querySelectorAll(".wp-block-cinetic-creatives-blocks-category")
        if (!block || items.length < 2) return

        const holder = document.createElement("div")
        holder.classList.add("category-captions")

        items.forEach((item) => {
            const caption = document.createElement("div")
            caption.classList.add("category-caption")
            const name = item.querySelector(":scope > .has-medium-font-size")
            const link = item.querySelector(":scope > a")
            if (name) caption.appendChild(name.cloneNode(true))
            if (link) caption.appendChild(link.cloneNode(true))
            // The originals were hidden by BatchAnimation (autoAlpha 0) before
            // being cloned; the clones are revealed by updateCaptions instead.
            gsap.set(caption.querySelectorAll(".wp-block-cinetic-creatives-blocks-call-to-action"), { clearProps: "opacity,visibility" })
            holder.appendChild(caption)
            this.captions.push(caption)
        })

        this.$graphics.appendChild(holder)
        block.classList.add("has-pinned-captions")
        this.updateCaptions(0)
    }

    updateCaptions(progress) {
        if (!this.captions || !this.captions.length) return
        // progress 0..1 spans (count - 1) video transitions; t is the slide position
        const t = progress * (this.captions.length - 1)
        this.captions.forEach((caption, i) => {
            // Fully visible within +-0.25 of the slide, gone by +-0.5, so two
            // captions never show at the same time in the same place.
            const opacity = gsap.utils.clamp(0, 1, (0.5 - Math.abs(t - i)) / 0.25)
            caption.style.opacity = opacity
            caption.classList.toggle("is-active", opacity > 0.5)
        })
    }

    // progress 0..1 spans (count - 1) crossfades. Each crossfade runs over the
    // middle 80% of its segment, like the original tween mapping.
    applyProgress(progress) {
        const textures = this.components.videoGrid.videoTextures
        const count = textures.length
        if (count === 0) return
        const uniforms = this.components.background.material.uniforms

        if (count === 1) {
            uniforms.tDiffuse.value = textures[0]
            uniforms.tDiffuse2.value = textures[0]
            uniforms.dispFactor.value = 0
        } else {
            const t = gsap.utils.clamp(0, count - 1, progress * (count - 1))
            const from = Math.min(Math.floor(t), count - 2)
            const frac = t - from
            const smoothStepFunc = gsap.utils.pipe(
                gsap.utils.clamp(0.1, 0.9),
                gsap.utils.mapRange(0.1, 0.9, 0, 1),
            )
            uniforms.tDiffuse.value = textures[from]
            uniforms.tDiffuse2.value = textures[from + 1]
            uniforms.dispFactor.value = smoothStepFunc(frac)

            const index = frac < 0.5 ? from : from + 1
            if (index !== this.currentIndex) {
                this.currentIndex = index
                if (this.htmlVideos) this.syncVideoAudio(this.htmlVideos)
            }
        }

        this.updateCaptions(progress)
    }

    initialize() {
        const { $graphics } = this;
        $graphics?.appendChild( this.renderer.domElement );

        this.camera.position.z = 3.14;
    }


    hanedleRender() {        
        this.camera.updateMatrixWorld();
        Object.keys(this.components).forEach(_ => {
            if ("handleRender" in this.components[_]) this.components[_].handleRender(this.pane);
        });


        this.renderer.render(this.scene, this.camera);
    }

    // Phones only. Safari sizes 100lvh to the screen with the toolbars hidden
    // from the start; Chrome and in-app browsers start with the toolbars shown
    // and ScrollTrigger ignores the height-only resize when they hide. Size the
    // holder and the three slides from the largest screen height seen, in
    // pixels, so they stay in step with each other, and re-measure once.
    fitViewport() {
        if (!this.$graphics.isConnected || !window.matchMedia("(hover: none), (pointer: coarse)").matches) return
        const width = window.innerWidth
        const screen = window.innerHeight
        const slides = this.$graphics.closest(".wp-block-cinetic-creatives-blocks-categories")?.querySelectorAll(".wp-block-cinetic-creatives-blocks-category") || []
        if (width !== this.fitWidth) {
            // orientation changed: back to the stylesheet values, ScrollTrigger refreshes on its own
            this.fitWidth = width
            this.fitScreen = screen
            this.$graphics.style.height = ""
            slides.forEach(el => el.style.height = "")
            return
        }
        if (screen <= this.fitScreen) return
        this.fitScreen = screen
        this.$graphics.style.height = screen + "px"
        slides.forEach(el => el.style.height = screen + "px")
        ScrollTrigger.refresh()
    }

    handleResize() {
        const width = this.$graphics.offsetWidth;
        const height = this.$graphics.offsetHeight;
        // Phone browsers fire resize every time their toolbars slide in or
        // out. The holder keeps its size then, and re-sizing the WebGL
        // canvas for nothing clears it for a frame (black flash, jitter).
        if (width === this.lastWidth && height === this.lastHeight) return
        this.lastWidth = width
        this.lastHeight = height
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize( width, height );

        Object.keys(this.components).forEach(_ => {
            if ("handleResize" in this.components[_]) this.components[_].handleResize(width, height);
        });
    }

    setDebug() {
        this.pane = new Pane();
        Object.keys(this.components).forEach(_ => {
            if ("setDebug" in this.components[_]) this.components[_].setDebug(this.pane);
        });
    }

    dispose() {
        this.st?.kill()
        Object.keys(this.components).forEach(_ => {
            if ("dispose" in this.components[_]) {
                this.components[_].dispose();
                delete this.components[_];
            }
        });
        // this.renderer.setAnimationLoop(null);
        // this.renderer.dispose();

        // ScrollTrigger.removeEventListener("refresh", this.boundResize)
        // window.removeEventListener("resize", this.boundResize);
        clearTimeout(this.fitTimer)
        window.removeEventListener("resize", this.boundFitViewport);

    }
}
